import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { useToast } from "../lib/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faTruckFast, faStore } from "@fortawesome/free-solid-svg-icons";
import type { Order } from "../lib/types";

const fmt = (n: number) => n.toLocaleString("ko-KR");

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  pending_payment: { text: "결제 대기", cls: "badge-warning" },
  paid:            { text: "결제 완료", cls: "badge" },
  preparing:       { text: "상품 준비", cls: "badge" },
  shipping:        { text: "배송 중",   cls: "badge-success" },
  pickup_ready:    { text: "수령 대기", cls: "badge-success" },
  completed:       { text: "거래 완료", cls: "badge-success" },
  cancelled:       { text: "취소",      cls: "badge-danger" },
};

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { add } = useCart();
  const { push } = useToast();
  const nav = useNavigate();

  useEffect(() => {
    api.get<{ items: Order[] }>("/orders").then((r) => setOrders(r.items)).finally(() => setLoading(false));
  }, []);

  // 재주문 — 주문 상세에서 항목 복구 후 장바구니에 담고 /cart 로 이동
  const reorder = async (o: Order) => {
    setBusyId(o.id);
    try {
      const { items } = await api.get<{ items: Array<{ product_id: string; sku_snapshot: string; name_snapshot: string; quantity: number }> }>(`/orders/${o.id}`);
      let added = 0, skipped = 0;
      for (const it of items) {
        // 상품 상세로 단가 재조회 (등급별 최신가 반영)
        try {
          const { product } = await api.get<{ product: any }>(`/products/${encodeURIComponent(it.sku_snapshot)}`);
          add(product, Math.max(it.quantity, product.min_order));
          added++;
        } catch { skipped++; }
      }
      push(`재주문 담기 완료 (성공 ${added}${skipped ? ` · 품절/삭제 ${skipped}` : ""})`, added > 0 ? "success" : "error");
      if (added > 0) nav("/cart");
    } catch (e: any) {
      push(e.message, "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main id="main-content" className="container" style={{ padding: "var(--s-8) var(--s-5)", maxWidth: 1000 }}>
      <p className="eyebrow" style={{ marginBottom: 4 }}>My Orders</p>
      <h1 className="h-title" style={{ marginBottom: 20 }}>주문 내역 <span className="muted" style={{ fontSize: 15, fontWeight: 400 }}>({orders.length})</span></h1>

      {loading ? (
        <div className="card" style={{ padding: "var(--s-5)" }}>
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skel" style={{ height: 44, marginBottom: 8 }} />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="card" style={{ padding: "var(--s-10)", textAlign: "center" }}>
          <p style={{ fontWeight: 600, fontSize: 15 }}>주문 내역이 없습니다.</p>
          <p className="muted" style={{ marginTop: 6 }}>카탈로그에서 필요한 자재를 둘러보세요.</p>
          <Link to="/catalog" className="btn btn-primary" style={{ marginTop: 16 }}>카탈로그 보기</Link>
        </div>
      ) : (
        <div className="card" style={{ overflowX: "auto" }}>
          <table>
            <thead><tr>
              <th>주문번호</th><th>주문일시</th><th>수령</th><th>결제</th><th>상태</th><th style={{ textAlign: "right" }}>금액</th><th></th>
            </tr></thead>
            <tbody>
              {orders.map((o) => {
                const s = STATUS_LABEL[o.status] ?? { text: o.status, cls: "badge-muted" };
                return (
                  <tr key={o.id}>
                    <td><Link to={`/orders/${o.id}`} className="mono" style={{ fontWeight: 600, color: "var(--primary)" }}>{o.order_no}</Link></td>
                    <td className="muted" style={{ fontSize: 15 }}>{new Date(o.created_at * 1000).toLocaleString("ko-KR")}</td>
                    <td style={{ fontSize: 15 }}>
                      <FontAwesomeIcon icon={o.fulfillment === "delivery" ? faTruckFast : faStore} style={{ width: 12, height: 12, marginRight: 4, color: "var(--text-muted)" }} />
                      {o.fulfillment === "delivery" ? "배송" : "수령"}
                    </td>
                    <td style={{ fontSize: 15 }}>{o.payment_method === "card" ? "카드" : o.payment_method === "bank_transfer" ? "계좌이체" : "세금계산서"}</td>
                    <td><span className={`badge ${s.cls}`}>{s.text}</span></td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(o.total)}원</td>
                    <td style={{ textAlign: "right" }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => reorder(o)} disabled={busyId === o.id} title="동일 항목으로 재주문">
                        <FontAwesomeIcon icon={faRotateRight} style={{ width: 11, height: 11 }} />
                        {busyId === o.id ? "담는 중..." : "재주문"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
